import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { subscriptionCheckoutService } from '@/services/customer/subscription-checkout.service';
import { customerPlanManagementKeys } from '@/hooks/customer/useCustomerPlanManagement';
import type {
  CustomerSubscriptionCheckoutConfirmRequest,
  CustomerSubscriptionCheckoutIntentRequest,
} from '@/types/customer-subscription-checkout';

export const customerSubscriptionCheckoutKeys = {
  all: ['customer', 'subscription', 'checkout'] as const,
  context: (params?: { planId?: string | null; priceId?: string | null }) =>
    [...customerSubscriptionCheckoutKeys.all, 'context', params ?? null] as const,
};

export function useCustomerSubscriptionCheckoutContext(
  params?: { planId?: string | null; priceId?: string | null },
  enabled = true,
) {
  return useQuery({
    queryKey: customerSubscriptionCheckoutKeys.context(params),
    queryFn: () => subscriptionCheckoutService.getCheckoutContext(params),
    staleTime: 30_000,
    enabled,
  });
}

export function useCreateCustomerSubscriptionCheckoutIntent() {
  return useMutation({
    mutationFn: (payload: CustomerSubscriptionCheckoutIntentRequest) =>
      subscriptionCheckoutService.createCheckoutIntent(payload),
  });
}

export function useConfirmCustomerSubscriptionCheckout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CustomerSubscriptionCheckoutConfirmRequest) =>
      subscriptionCheckoutService.confirmCheckout(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customerPlanManagementKeys.all });
      await queryClient.invalidateQueries({ queryKey: customerSubscriptionCheckoutKeys.all });
    },
  });
}
